import React, { Component, useCallback, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { withRouter, Link, useLocation } from 'react-router-dom';
import { useTranslation, withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { mapObjectize } from 'cityads-v3/utils/apiUtils';
import { arrayIncludesValue } from 'cityads-v3/utils/arrayUtils';
import config from 'cityads-v3/config';
import { amplitudeSendEvent } from 'cityads-v3/utils/counterUtils';
import { parse } from 'query-string'
import withHooks from 'cityads-v3/decorators/withHooks';
import { find, difference } from 'lodash'
import { useThemeChanger } from 'cityads-v3/hooks/theme'

import { Helmet } from 'react-helmet';
import Balaclava from 'cityads-v3/components/Balaclava';
import DocumentationForm from 'cityads-v3/components/DocumentationForm';
import PageWithMenu from 'cityads-v3/components/PageWithMenu';
import Confirm from 'cityads-v3/components/Dialog/Confirm';
import QuestionIcon from './img/question.js';

import AuthIFrame from 'cityads-v3/containers/Auth/AuthIFrame';

import CodeExample from './CodeExample';
import OfferPayments from 'cityads-v3/containers/webmaster/offers/OfferPayments';

import {
  fetchEntity
} from 'cityads-v3/actions/entity';

import Card from 'cityads-v3/components/Card/Card';
import EmptyResults from 'cityads-v3/components/EmptyResults/EmptyResults';
import FilterCheck from 'cityads-v3/components/Filter/FilterCheck';
import Subaccounts from 'cityads-v3/components/Forms/Subaccounts';
import Table from 'cityads-v3/components/Table/Table';
import Tooltip from 'cityads-v3/components/Tooltip/Tooltip';

import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import EnumList from './EnumList';
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popover from '@mui/material/Popover'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography';

import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownOutlinedIcon from '@mui/icons-material/ArrowDropDownOutlined'
import ArrowDropUpOutlinedIcon from '@mui/icons-material/ArrowDropUpOutlined'
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import classes from './Firano.scss';

const CONSTRUCTOR_TYPE_OFFERS = 'offers';
const CONSTRUCTOR_TYPE_FIELDS = 'fields';

const HEADER_OFFSET = 16;
const OFFER_TABLE_COLUMNS = (t, CheckBoxFormatter, HeaderCheckboxFormatter) => ([
  {
    title: <HeaderCheckboxFormatter/>,
    name: 'checkbox',
    formatter: CheckBoxFormatter,
    dense: true,
    component: 'Checkbox'
  },
  {
    title: t('offerTable.offerId'),
    name: 'offerId',
  }, {
    title: t('offerTable.offerName'),
    name: 'offerName',
  }, {
    title: t('offerTable.productName'),
    name: 'productName',
  }, {
    title: t('Offers:payment'),
    name: 'payments'
  }
]);

const FIELD_TABLE_COLUMNS = (t, constructorType, CheckBoxFormatter, HeaderCheckboxFormatter) => ([
  ...(constructorType === CONSTRUCTOR_TYPE_FIELDS ? [{
    title: <HeaderCheckboxFormatter/>,
    name: 'checkbox',
    formatter: CheckBoxFormatter,
    dense: true
  }] : []),
  {
    title: t('fieldTable.offersCount'),
    name: 'offersCount',
  },
  {
    title: t('fieldTable.id'),
    name: 'id',
  }, {
    title: t('fieldTable.type'),
    name: 'type',
  }, {
    title: t('fieldTable.name'),
    name: 'name',
  }, {
    title: t('fieldTable.values'),
    name: 'values',
  }, {
    title: t('fieldTable.example'),
    name: 'example',
  }
]);

function plainFields(fields) {
  const plainArray = [];

  fields.forEach(field => {
    if (field.key && field.value) {
      plainArray.push(field.key);
      field.value.forEach(value => {
        plainArray.push(`${field.key}.${value}`);
      });
    } else {
      plainArray.push(field);
    }
  });
  return plainArray;
}

function parseOffers({ offers, products, productGroups }) {
  const productGroupsById = mapObjectize(productGroups, 'id', 'items', false);
  const productsById = mapObjectize(products, 'id', 'items', false);
  const offersById = mapObjectize(offers, 'id', 'items', false);

  offers.forEach(offer => {
    offer.plainFields = plainFields(offer.fields);

    if (productsById[offer.product]?.offerIds.includes(offer.id) === false) {
      productsById[offer.product].offerIds.push(offer.id);
    }
  });

  productGroups.forEach(productGroup => {
    productGroup.productIds = [];
  });
  products.forEach(product => {
    if (product.offerIds.length) {
      productGroupsById[product.group]?.productIds.push(product.id);
    }
  });

  return {
    offers,
    offersById,
    products,
    productsById,
    productGroups,
    productGroupsById,
  }
}

function getFieldRow({
  field,
  child,
  t,
  location,
  constructorType,
  productsById,
  offersById,
  availableOffers,
  showFields,
  selectedFields,
  unsatisfiedOffersByField,
  satisfiedOffersByField,
  expandedSubrowsByField,
  onToggleFieldExpanded,
}) {
  const current = child || field;
  const currentId = child ? `${field.id}.${current.id}` : current.id;

  const offersWithThisField = showFields[currentId] || [];

  const isFieldsConstructor = constructorType === CONSTRUCTOR_TYPE_FIELDS;
  const isSelected = selectedFields.indexOf(currentId) >= 0;
  const isRequired = offersWithThisField.length === availableOffers.length;
  const isExpanded = expandedSubrowsByField[currentId];
  const isParent = field.children && !child;

  const values = typeof current.values === 'object' ?
    Object.keys(current.values).join(', ') :
    current.values;

  const example = typeof current.example === 'object' ?
    current.example[current.id] :
    current.example;

  return [
    {
      offersCount: (
        <span className={classes.offersCount}>
          <Tooltip
            placement = "right"
            title     = {t('requiringOffers', {
              offers: offersWithThisField.map(offerId => offersById[offerId]?.name || `#${offerId}`).join(', '),
              count: offersWithThisField.length
            })}
          >
            <span>{offersWithThisField.length}</span>
          </Tooltip>

          {(!isFieldsConstructor || isRequired || isParent) ?
            null:
            (isSelected ?
              (
                satisfiedOffersByField[currentId]?.length ?
                  (
                    <Tooltip
                      placement = "right"
                      title     = {t('satisfiedOffers', { count: satisfiedOffersByField[currentId].length })}
                    >
                      <span className={classes.satisfied}>+{satisfiedOffersByField[currentId].length}</span>
                    </Tooltip>
                  ) :
                  null
              ) : (
                unsatisfiedOffersByField[currentId]?.length ?
                  (
                    <Tooltip
                      placement = "right"
                      title     = {t('unsatisfiedOffers', { count: unsatisfiedOffersByField[currentId].length })}
                    >
                      <span className={classes.unsatisfied}>&minus;{unsatisfiedOffersByField[currentId].length}</span>
                    </Tooltip>
                  ) :
                  null
              )
            )
          }

          {(isFieldsConstructor && !isRequired && !isParent && (isSelected ? satisfiedOffersByField[currentId]?.length : unsatisfiedOffersByField[currentId]?.length)) ?
            (
              <IconButton
                color     = "primary"
                size      = "small"
                className = {classNames(classes.expander, { [classes.expanderExpanded]: isExpanded })}
                onClick   = {() => onToggleFieldExpanded(currentId)}
              >
                <ArrowDropDownOutlinedIcon />
              </IconButton>
            ) :
            null
          }
        </span>
      ),
      id: currentId,
      type: current.type,
      name: current.name || current.title,
      values: current.type === 'enum' ? <EnumList values={current.values}>{values}</EnumList> : values,
      example,
      classes: {
        root: isParent ? classes.tableRowHasChildren : ''
      }
    },
    ...((isFieldsConstructor && !isParent && isExpanded) ?
      (isSelected ? satisfiedOffersByField : unsatisfiedOffersByField)[currentId]?.map(offerId => ({
        key: `${currentId}.${offerId}`,

        _style: { backgroundColor: isSelected ? 'hsl(116deg 100% 65% / 20%)' : 'hsl(13deg 100% 65% / 20%)' },

        isDependentOffer: true,

        offersCount: (
          <>
            {t('offerTable.offerId')}:
            {' '}
            <Link
              to = {{
                pathname: `/publisher/offer/${offerId}`,
                state   : {
                  source      : 'firano',
                  sourceSearch: location.search,
                },
              }}
            >
              {offerId}
            </Link>
          </>
        ),
        id  : `${t('offerTable.offerName')}: ${offersById[offerId]?.name || ''}`,
        type: `${t('offerTable.productName')}: ${productsById[offersById[offerId]?.product]?.name || ''}`,
      })) || [] :
      []
    )
  ];
}

@withHooks(props => {
  const { search } = useLocation();
  const queryParams = parse(search);

  return {
    ...props,
    themeChanger: useThemeChanger(),
    selectedOfferId: +queryParams.offerId
  }
})
@withRouter
@connect(
  state => {
    const parsed = parseOffers({
      offers: state.entities.financialOffers?.data || [],
      products: state.entities.financeProduct?.data || [],
      productGroups: state.entities.financeProductGroup?.data || [],
    });

    const fields = state.entities.financeOfferField?.data || [];
    const fieldsById = mapObjectize(fields, 'id');

    return {
      ...parsed,
      fields,
      fieldsById,
      isFetchingOffer: state.entities.financialOffers?.isFetching,
      isFetchingOfferField: state.entities.financeOfferField?.isFetching,
      isFetchingProducts: state.entities.financeProduct?.isFetching,
      isFetchingProductGroups: state.entities.financeProductGroup?.isFetching,
      language: state.language.language,
    }
  }
)
@withTranslation(['Finance', 'Offers', 'main'], { i18n, wait: false })
export default class Firano extends Component {

  static propTypes = {
    offers: PropTypes.array,
    offersById: PropTypes.object,
    products: PropTypes.array,
    productsById: PropTypes.object,
    productGroups: PropTypes.array,
    productGroupsById: PropTypes.object,
    language: PropTypes.string,

    isFetchingOffer: PropTypes.bool,
    isFetchingOfferField: PropTypes.bool,
    isFetchingProducts: PropTypes.bool,
    isFetchingProductGroups: PropTypes.bool,
  }

  state = {
    constructorType: this.props.selectedOfferId ? CONSTRUCTOR_TYPE_OFFERS : CONSTRUCTOR_TYPE_FIELDS,
    is_agree_personal: true,
    sandbox: false,
    selectedFields: [],
    selectedOffers: this.props.selectedOfferId ? [this.props.selectedOfferId] : [],
    availableOffers: [],
    unsatisfiedOffersByField: {},
    satisfiedOffersByField: {},
    expandedSubrowsByField: {},
    selectedProductGroupId: null,
    selectedProducts: [],
    showFields: {},
    showOffers: [],
    subaccounts: [''],
    showRequestRefreshTokenDialog: false,
    showRequestRefreshTokenLoader: true,
  }

  componentDidMount() {
    this.loadEntities();
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    const { isFetchingProducts, isFetchingProductGroups, isFetchingOffer, isFetchingOfferField, language } = this.props;

    if (
      (isFetchingProducts && newProps.isFetchingProducts === false) ||
      (isFetchingProductGroups && newProps.isFetchingProductGroups === false) ||
      (isFetchingOffer && newProps.isFetchingOffer === false) ||
      (isFetchingOfferField && newProps.isFetchingOfferField === false)
    ) {
      this.selectAllProducts(newProps);
    }

    if (language !== newProps.language) {
      this.loadEntities(newProps);
    }
  }

  loadEntities(props = this.props) {
    const { dispatch } = props;

    dispatch(fetchEntity({api: 'financialOffers'}));
    dispatch(fetchEntity({api: 'financeOfferField'}));
    dispatch(fetchEntity({api: 'financeProduct'}));
    dispatch(fetchEntity({api: 'financeProductGroup'}));
  }

  getOffset() {
    if (window) {
      const headerEl = window.document.getElementById('header');
      return headerEl ? -headerEl.offsetHeight - HEADER_OFFSET : 0;
    }

    return 0;
  }

  handleChangeConstructorType = (evt, constructorType) => {
    this.setState({
      constructorType,
      selectedOffers: [],
      showFields: {},
      showOffers: [],
      selectedProductGroupId: null,
      selectedProducts: [],
      selectedFields: [],
    }, this.selectAllProducts);

    amplitudeSendEvent('makeActions', 'd-v3-e1', 'firano', 'tab_selection', {
      tab: constructorType
    });
  }

  handleProductGroupChange = evt => {
    const { constructorType } = this.state;
    const { productGroupsById } = this.props;
    const productGroupId = evt.target.value;

    this.setState({
      selectedProductGroupId: Number(productGroupId),
      selectedProducts: [],
    }, this.selectAllProducts);

    amplitudeSendEvent('makeActions', 'd-v3-e2', 'firano', 'product_group_selection', {
      tab: constructorType,
      radiobutton: productGroupsById[productGroupId]?.code
    });
  }

  handleProductChange = value => {
    const { constructorType, selectedProducts } = this.state;
    const { productsById } = this.props;

    this.setState({
      selectedProducts: value || []
    }, constructorType === CONSTRUCTOR_TYPE_OFFERS ?
      this.selectOffersBySelectedProducts
      :
      this.selectFieldsBySelectedProducts
    );

    const added = difference(value, selectedProducts).map(id => productsById[id]?.code)
    const deleted = difference(selectedProducts, value).map(id => productsById[id]?.code)
    amplitudeSendEvent('makeActions', 'd-v3-e3', 'firano', 'products_change', {
      tab: constructorType,
      add_product: added,
      delete_product: deleted,
    });
  }

  handleProductRemove = removeId => () => {
    const { selectedProducts, constructorType } = this.state;
    const { productsById } = this.props;

    this.setState({
      selectedProducts: selectedProducts.filter(id => id !== removeId),
    }, () => {
      if (constructorType === CONSTRUCTOR_TYPE_OFFERS) {
        this.selectOffersBySelectedProducts();
      } else {
        this.selectFieldsBySelectedProducts();
      }
    });

    amplitudeSendEvent('makeActions', 'd-v3-e3', 'firano', 'products_change', {
      tab: constructorType,
      add_product: [],
      delete_product: [productsById[removeId]?.code],
    });
  }

  handleSelectAllOffers = evt => {
    const { constructorType, showOffers } = this.state;
    const { checked } = evt.target;
    const newOfferList = checked ? [...showOffers] : [];

    this.setState({
      selectedOffers: newOfferList
    }, constructorType === CONSTRUCTOR_TYPE_OFFERS ? this.selectFieldsBySelectedOffers : () => { });

    amplitudeSendEvent('makeActions', 'd-v3-e4', 'firano', 'checkboxes_change', {
      tab: constructorType,
      checked: newOfferList,
      unchecked: showOffers.filter(offer => !newOfferList.includes(offer))
    });
  }

  handleOfferChange = evt => {
    const { selectedOffers, constructorType } = this.state;
    const offerId = +evt?.target.value;
    const checked = evt?.target.checked;
    let newOfferList;
    if (offerId) {
      newOfferList = checked ? [...selectedOffers, offerId] : selectedOffers.filter(id => id !== offerId);
    } else {
      newOfferList = selectedOffers;
    }

    this.setState({
      selectedOffers: newOfferList
    }, constructorType === CONSTRUCTOR_TYPE_OFFERS ? this.selectFieldsBySelectedOffers : () => { });

    amplitudeSendEvent('makeActions', 'd-v3-e4', 'firano', 'checkboxes_change', {
      tab: constructorType,
      checked: checked ? [offerId] : [],
      unchecked: checked ? [] : [offerId]
    });
  }

  handleFieldChange = evt => {
    const { selectedFields, constructorType } = this.state;
    const { fieldsById } = this.props;
    const fieldId = evt.target.value;
    const fieldSplit = fieldId.split('.');
    const checked = evt.target.checked;
    let newFieldList = [...selectedFields];

    if (fieldSplit[1]) {

      if (checked && newFieldList.indexOf(fieldSplit[0]) === -1) {
        newFieldList = [
          ...newFieldList,
          fieldSplit[0]
        ];
      } else if ( !checked && newFieldList.filter(field => field.indexOf(`${fieldSplit[0]}.`) !== -1).length === 1 ) {
        newFieldList = newFieldList.filter(field => field !== fieldSplit[0]);
      }

    }

    newFieldList = checked ? [...newFieldList, fieldId] : newFieldList.filter(id => id !== fieldId);

    if (fieldsById[fieldId] && fieldsById[fieldId].children) {
      const fields = fieldsById[fieldId].children.map(field => `${fieldId}.${field.id}`);
      newFieldList = checked ? [...newFieldList, ...fields] : newFieldList.filter(id => fields.indexOf(id) === -1);
    }

    this.setState({
      selectedFields: newFieldList
    }, this.selectShowOffersBySelectedFields);

    amplitudeSendEvent('makeActions', 'd-v3-e4', 'firano', 'checkboxes_change', {
      tab: constructorType,
      checked: checked ? [fieldId] : [],
      unchecked: checked ? [] : [fieldId]
    });
  }

  handleSelectAllFields = evt => {
    const { showFields, availableOffers, constructorType } = this.state;
    const { checked } = evt.target;
    const availableFields = Object.keys(showFields);
    const newFieldList = checked ? availableFields : availableFields.filter(field => showFields[field]?.length === availableOffers.length);

    this.setState({
      selectedFields: newFieldList
    }, this.selectShowOffersBySelectedFields)

    amplitudeSendEvent('makeActions', 'd-v3-e4', 'firano', 'checkboxes_change', {
      tab: constructorType,
      checked: newFieldList,
      unchecked: availableFields.filter(field => !newFieldList.includes(field))
    });
  }

  handleChangeCheckbox = key => (evt, checked) => {
    this.setState({
      [key]: checked
    });

    if (key === 'sandbox') {
      amplitudeSendEvent(
        'makeActions',
        checked ? 'd-v1-e30' : 'd-v1-e31',
        'firano',
        checked ? 'sub_id_sandbox_on' : 'sub_id_sandbox_off'
      );
    }
  }

  handleSubaccountsChange = value => {
    this.setState({
      subaccounts: value
    })
  }

  handleSubaccountsAdd = length => {
    amplitudeSendEvent('makeActions', 'd-v1-e29', 'firano', 'sub_id_add', {
      sub_id_number: length
    });
  }

  handleCodeExampleCopy = type => {
    const { productGroupsById } = this.props;
    const {
      constructorType,
      selectedFields,
      selectedOffers,
      selectedProductGroupId,
      selectedProducts
    } = this.state;

    amplitudeSendEvent(
      'makeActions',
      type === 'json' ? 'd-v1-e32' : 'd-v1-e33',
      'firano',
      type === 'json' ? 'json_copy' : 'full_code_copy',
      {
        tab: constructorType,
        selectedFields,
        selectedOffers,
        selectedProducts,
        radiobutton: productGroupsById[selectedProductGroupId]?.code,
      }
    );
  }

  handleToggleFieldExpanded = field => {
    this.setState(state => ({
      expandedSubrowsByField: {
        ...state.expandedSubrowsByField,
        [field]: !state.expandedSubrowsByField[field],
      }
    }));
  }

  selectOffersBySelectedProducts() {
    const { selectedProducts, selectedOffers, showFields } = this.state;
    const { productsById, selectedOfferId } = this.props;
    let showOffers = {};

    selectedProducts.forEach(productId => {
      const product = productsById[productId];
      product.offerIds.forEach(offerId => {
        showOffers = {
          ...showOffers,
          [offerId]: true
        }
      })
    });

    showOffers = Object.keys(showOffers).map(Number);

    const newSelectedOffers = selectedOffers.filter(id => showOffers.indexOf(id) !== -1);
    if (selectedOfferId && !newSelectedOffers.includes(selectedOfferId)) {
      newSelectedOffers.push(selectedOfferId);
    }

    this.setState({
      showOffers,
      selectedOffers: newSelectedOffers,
      showFields: Object.keys(this.state.showFields).reduce((prev, next) => ({
        ...prev,
        [next]: showFields[next].filter(id => showOffers.indexOf(id) !== -1)
      }), {}),
    }, this.handleFirstSelectCurrentOffer);
  }

  handleFirstSelectCurrentOffer() {
    const { firstSelectCurrentOffer } = this.state;
    const { productsById, selectedOfferId, offers, history } = this.props;

    const firstProductInSelectedOffer = selectedOfferId ? find(offers, {id: selectedOfferId})?.product: null;
    const newSelectedProductGroupId = productsById[firstProductInSelectedOffer]?.group;

    if (!firstSelectCurrentOffer && newSelectedProductGroupId) {
      this.setState({
        selectedProductGroupId: newSelectedProductGroupId,
        firstSelectCurrentOffer: true
      });

      this.handleOfferChange();
      history.replace('/publisher/firano');
    }
  }

  selectFieldsBySelectedOffers(selectedOffers = this.state.selectedOffers) {
    const { offersById } = this.props;
    const { constructorType, availableOffers } = this.state;

    let showFields = {};
    let selectedFields = [];

    selectedOffers.forEach(offerId => {
      const offer = offersById[offerId];
      const fieldsObj = offer?.plainFields.reduce((prev, next) => ({
        ...prev,
        [next]: showFields[next] ? [...showFields[next], offerId] : [offerId]
      }), {});

      showFields = {
        ...showFields,
        ...fieldsObj
      }
    });

    Object.entries(showFields).forEach(([ field, fieldOfferIds ]) => {
      if (fieldOfferIds.length === availableOffers.length) {
        selectedFields.push(field);
      }
    });

    this.setState({
      showFields,
      selectedFields,
    }, constructorType === CONSTRUCTOR_TYPE_FIELDS ? this.selectShowOffersBySelectedFields : null);

  }

  selectFieldsBySelectedProducts() {
    const { selectedProducts } = this.state;
    const { productsById } = this.props;

    let selectedOffers = {};

    selectedProducts.forEach(productId => {
      productsById[productId].offerIds.forEach(offerId => {
        selectedOffers[offerId] = true;
      });
    });

    const availableOffers = Object.keys(selectedOffers).map(Number);

    this.setState({
      availableOffers,
      expandedSubrowsByField: {},
    }, () => this.selectFieldsBySelectedOffers(availableOffers));
  }

  selectShowOffersBySelectedFields() {
    const { selectedFields, selectedProducts, showFields, availableOffers } = this.state;
    const { offers, offersById } = this.props;

    const showOffers = offers.filter(offer => {
      return arrayIncludesValue(selectedFields, offer.plainFields) &&
        arrayIncludesValue(selectedProducts, offer.product);
    }).map(offer => offer.id);

    const missingFieldsByOfferId = availableOffers.reduce((acc, offerId) => {
      const offer = offersById[offerId];

      return {
        ...acc,
        [offerId]: offer?.plainFields.filter(field => selectedFields.indexOf(field) < 0) || [],
      };
    }, {});

    let unsatisfiedOffersByField = {};
    let satisfiedOffersByField = {};

    Object.keys(showFields).forEach(field => {
      unsatisfiedOffersByField[field] = [];
      satisfiedOffersByField[field] = [];

      showFields[field].forEach(offerId => {
        const missingFields = missingFieldsByOfferId[offerId];

        if (missingFields.length === 1 && missingFields[0] === field) {
          unsatisfiedOffersByField[field].push(offerId);
        } else if (missingFields.length === 0) {
          satisfiedOffersByField[field].push(offerId);
        }
      });
    });

    this.setState({
      showOffers,
      selectedOffers: showOffers,
      unsatisfiedOffersByField,
      satisfiedOffersByField,
    });
  }

  selectAllProducts(props = this.props) {
    const { productsById, productGroups, productGroupsById } = props;
    const { selectedProductGroupId, constructorType } = this.state;

    const selectedProductGroup = selectedProductGroupId ?
      productGroupsById[selectedProductGroupId] :
      productGroups.filter(productGroup => productGroup.productIds.length)[0];

    const groupProductsIds = selectedProductGroup?.productIds.filter(id => productsById[id]?.offerIds.length).map(id => Number(id));

    this.setState(
      {
        selectedProductGroupId: Number(selectedProductGroup?.id) || null,
        selectedProducts: groupProductsIds || [],
      },
      constructorType === CONSTRUCTOR_TYPE_OFFERS ?
        this.selectOffersBySelectedProducts :
        this.selectFieldsBySelectedProducts
    );

  }

  getOfferTableRows() {
    const { productsById, offersById } = this.props;
    const { showOffers } = this.state;

    return showOffers.map(offerId => ({
      id: offerId,
      offerId: (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`/publisher/offer/${offerId}`}
        >{offerId}</a>
      ),
      offerName: (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${config.API_FIRANO_URL}/docs/webmasters/api/rest/bank/${offersById[offerId]?.code}`}
        >
          {offersById[offerId]?.name}
          <OpenInNewOutlinedIcon classes={{root: classes.openInNewIcon}} />
        </a>
      ),
      productName: productsById[offersById[offerId]?.product]?.name,
      payments: <OfferPayments offerId={offerId} />,
    }))
  }

  getFieldTableRows() {
    const {
      constructorType,
      availableOffers,
      showFields,
      selectedFields,
      unsatisfiedOffersByField,
      satisfiedOffersByField,
      expandedSubrowsByField,
    } = this.state;
    let { fields, t, location, productsById, offersById } = this.props;
    let rows = [];

    fields = fields.filter(field => showFields[field.id]);

    fields
      .forEach(field => {
        rows.push(...getFieldRow({
          field,
          t,
          location,
          constructorType,
          productsById,
          offersById,
          availableOffers,
          showFields,
          selectedFields,
          unsatisfiedOffersByField,
          satisfiedOffersByField,
          expandedSubrowsByField,
          onToggleFieldExpanded: this.handleToggleFieldExpanded,
        }));

        let children = field.children;

        if (field.children) {
          children = children.filter(children => showFields[`${field.id}.${children.id}`]);

          children.forEach(child => {
            rows.push(...getFieldRow({
              field,
              child,
              t,
              location,
              constructorType,
              productsById,
              offersById,
              availableOffers,
              showFields,
              selectedFields,
              unsatisfiedOffersByField,
              satisfiedOffersByField,
              expandedSubrowsByField,
              onToggleFieldExpanded: this.handleToggleFieldExpanded,
            }));
          });
        }
      });

    return rows;
  }

  getOfferHeaderCheckboxFormatter = () => {
    const { showOffers, selectedOffers } = this.state;
    const checked = showOffers.length === selectedOffers.length;
    return <Checkbox
      checked={checked}
      indeterminate={selectedOffers.length > 0 && !checked}
      onChange={this.handleSelectAllOffers}
    />
  }

  getOfferCheckboxFormatter = (value, row) => {
    const { selectedOffers } = this.state;

    return <Checkbox
      checked={selectedOffers.indexOf(row.id) !== -1}
      onChange={this.handleOfferChange}
      value={String(row.id)}
    />
  }

  getFieldHeaderCheckboxFormatter = () => {
    const { showFields, selectedFields } = this.state;
    const checked = selectedFields.length > 0 && Object.keys(showFields).length === selectedFields.length;
    return <Checkbox
      checked={checked}
      indeterminate={selectedFields.length > 0 && !checked}
      onChange={this.handleSelectAllFields}
    />
  }

  getFieldCheckboxFormatter = (value, row) => {
    if (row.isDependentOffer) {
      return null;
    }

    const { showFields, availableOffers, selectedFields } = this.state;

    return <Checkbox
      checked={selectedFields.indexOf(row.id) !== -1}
      disabled={showFields[row.id]?.length === availableOffers.length}
      onChange={this.handleFieldChange}
      value={row.id}
    />
  }

  renderExampleCode = () => {
    const {
      constructorType,
      showFields,
      showOffers,
      is_agree_personal,
      selectedFields,
      selectedOffers,
      selectedProducts,
      selectedProductGroupId,
      subaccounts
    } = this.state;
    const { fields, t, productsById, productGroupsById } = this.props;

    const productGroup = productGroupsById[selectedProductGroupId];

    let json = {
      is_agree_personal,
      products: selectedProducts.map(product => productsById[product].code),
      ...subaccounts.reduce((prev, next, idx) => ({
        ...prev,
        ...(next ? {[idx ? `sub_id_${idx+1}`:'sub_id']: next} : {})
      }), {})
    };

    const filterFields = constructorType === CONSTRUCTOR_TYPE_OFFERS ? showFields : selectedFields.reduce((prev, next) => ({ ...prev, [next]: true }), {});
    const exclude_offer_ids = showOffers.filter(offer => selectedOffers.indexOf(offer) === -1);

    fields
      .filter(field => filterFields[field.id])
      .forEach(field => {
        json = {
          ...json,
          [field.id]: field.children ? {} : field.example
        }

        if (field.children) {
          field.children
            .filter(child => filterFields[`${field.id}.${child.id}`])
            .forEach(child => {
              json = {
                ...json,
                [field.id]: {
                  ...json[field.id],
                  [child.id]: typeof child.example === 'object' ? child.example[child.id] : child.example
                }
              }
            })
        }
      });

    return (
      <CodeExample
        product_group={productGroup?.code || null}
        offers={constructorType === CONSTRUCTOR_TYPE_OFFERS ? selectedOffers : []}
        exclude_offer_ids={constructorType === CONSTRUCTOR_TYPE_FIELDS ? exclude_offer_ids : []}
        sandbox={this.state.sandbox}
        t={t}
        json={json}
        onCopy={this.handleCodeExampleCopy}
      />
    );
  }

  getGeoUrl = () => {
    const { selectedOffers } = this.state;
    const banks = selectedOffers.map(id => 'offer_ids[]=' + id).join('&');
    const url = `${config.FIRANO_URL}/docs/webmasters/api/rest/export/geo/?${banks}&format=xls`;
    return url;
  }

  renderConstructorOffers() {
    const { constructorType, selectedOffers, showOffers } = this.state;
    const { t } = this.props;

    const offerTableRows = this.getOfferTableRows();

    return (
      <>
        <div className={classes.constructor__content}>
          <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
            {t('blockConstructor.offers')}

            {constructorType === CONSTRUCTOR_TYPE_OFFERS && selectedOffers.length ? `(${selectedOffers.length})` : false}
            {constructorType === CONSTRUCTOR_TYPE_FIELDS && showOffers.length ? `(${showOffers.length})` : false}
          </Typography>
        </div>

        {offerTableRows.length ? (
          <Table
            scrollX
            cols = {OFFER_TABLE_COLUMNS(t, this.getOfferCheckboxFormatter, this.getOfferHeaderCheckboxFormatter)}
            rows={offerTableRows}
            tableHeadClasses={{
              root: classes.tableHead
            }}
          />
        ) : (
          <EmptyResults
            className={classes.emptyResults}
            icon={<ArrowDropUpOutlinedIcon />}
            emptyText={t('blockConstructor.offersEmptyText')}
            elevation={0}
            textGutterTop={false}
          />
        )}
      </>
    );
  }

  renderConstructorFields() {
    const { selectedOffers, constructorType } = this.state;
    const { t, } = this.props;
    const fieldTableRows = this.getFieldTableRows();

    return (
      <>
        <div className={classes.constructor__content}>
          <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
            {t('blockConstructor.fields')}
          </Typography>
        </div>

        {constructorType === CONSTRUCTOR_TYPE_FIELDS || selectedOffers.length ? (
          <Table
            scrollX
            cols = {FIELD_TABLE_COLUMNS(t, constructorType, this.getFieldCheckboxFormatter, this.getFieldHeaderCheckboxFormatter)}
            rows={fieldTableRows}
            tableHeadClasses={{
              root: classes.tableHead
            }}
          />
        ) : (
          <EmptyResults
            className={classes.emptyResults}
            icon={<PlaylistAddCheckOutlinedIcon />}
            emptyText={<>{t('blockConstructor.fieldsEmptyText1')}<br/>{t('blockConstructor.fieldsEmptyText2')}</>}
            elevation={0}
          />
        )}
      </>
    );
  }

  onAuthIframeMount({ iframe }) {
    this.authIframe = iframe;
  }

  onMessage(evt) {
    let state = {};
    if (evt.data.event === 'requestRefreshToken') {
      state.showRequestRefreshTokenDialog = true;
    } else if (evt.data.event === 'init') {
      state.showRequestRefreshTokenLoader = false;
    }

    this.setState(state);
  }

  closeRequestRefreshTokenDialog(showLoader = false) {
    this.setState({
      showRequestRefreshTokenDialog: false,
      showRequestRefreshTokenLoader: showLoader
    })
  }

  requestRefreshToken() {
    this.authIframe.contentWindow.postMessage({
      widget: 'firano_token',
      event: 'requestRefreshToken',
      accepted: true
    }, '*');

    this.closeRequestRefreshTokenDialog(true);
  }

  render() {
    const {
      t,
      productsById,
      productGroups,
    } = this.props;

    const {
      selectedOffers,
      selectedProducts,
      selectedProductGroupId,
      showRequestRefreshTokenDialog,
      showRequestRefreshTokenLoader,
    } = this.state;

    return (
      <>
        <Helmet>
          <title>{t('title')}</title>
        </Helmet>

        <Balaclava
          title = {t('title')}
        />

        <PageWithMenu>
          <Card
            title = {t('titles.description')}
            data-page-menu-key   = {'description'}
            data-page-menu-icon  = {<DescriptionOutlinedIcon />}
            data-page-menu-title = {t('titles.description')}
          >
            <>
              <Typography component="p">
                {t('blockDescription.description')}
              </Typography>

              <DocumentationForm
                className = {classes.docForm}
                documentationLink    = {`${config.API_FIRANO_URL}/docs/webmasters/api/rest/universal`}
                documentationContent = {(
                  <>
                    <Typography variant="body2" paragraph={true}>
                      {t('blockDescription.docdesc1')}<br/>
                    </Typography>
                    <Typography variant="body2" paragraph={true}>
                      {t('blockDescription.docdesc2')}
                    </Typography>
                  </>
                )}
              >
                <Typography gutterBottom variant="h6" component="h3">
                  {t('blockDescription.restTitle')}

                  {showRequestRefreshTokenLoader && (
                    <CircularProgress className={classes.tokenIframeLoader} size={18} />
                  )}
                </Typography>

                <AuthIFrame
                  className={classes.tokenIFrame}
                  src={config.FIRANO_URL + `/forms/token?theme=${this.props.themeChanger.theme.palette.mode}`}
                  title={t('blockDescription.iframeTitle')}
                  onEvent={this.onMessage.bind(this)}
                  onMount={this.onAuthIframeMount.bind(this)}
                />
              </DocumentationForm>

              <Confirm
                open={showRequestRefreshTokenDialog}
                yes={t('requestTokenDialog.yesButton')}
                no={t('main:buttons.cancel')}
                title={t('requestTokenDialog.title')}
                text={
                  <>
                    <QuestionIcon className={classes.questionIcon} />
                    {t('requestTokenDialog.text')}
                  </>
                }
                onClose={this.closeRequestRefreshTokenDialog.bind(this, false)}
                onNo={this.closeRequestRefreshTokenDialog.bind(this, false)}
                onYes={this.requestRefreshToken.bind(this)}
              />
            </>
          </Card>

          <Card
            title = {t('titles.params')}
            data-page-menu-key   = {'params'}
            data-page-menu-icon  = {<SettingsOutlinedIcon />}
            data-page-menu-title = {t('titles.params')}
          >
            <div className={classes.thirdGrid}>
              <Subaccounts
                maxLength={255}
                onChange={this.handleSubaccountsChange}
                placeholder={num => num ? `sub_id_${num+1}` : 'sub_id'}
                tooltip={t('blockParams.subaccountHint')}
                value={this.state.subaccounts}
                className={classes.thirdGrid__subaccounts}
                onAdd={this.handleSubaccountsAdd}
              />
              <div className={classes.thirdGrid__flags}>

                <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
                  {t('blockParams.sandboxTitle')}
                  <Tooltip title={t('blockParams.sandboxHint')}>
                    <InfoOutlinedIcon color="action" classes={{ root: classes.infoIcon }} />
                  </Tooltip>
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={this.state.sandbox}
                      onChange={this.handleChangeCheckbox('sandbox')}
                    />
                  }
                  label="sandbox"
                />

                <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
                  {t('blockParams.personalAgreeTitle')}
                  <Tooltip title={t('blockParams.personalAgreeHint')}>
                    <InfoOutlinedIcon color="action" classes={{ root: classes.infoIcon }} />
                  </Tooltip>
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      disabled
                      color="primary"
                      checked={this.state.is_agree_personal}
                      onChange={this.handleChangeCheckbox('is_agree_personal')}
                    />
                  }
                  label={<>is_agree_personal <LockOutlinedIcon color="action" classes={{ root: classes.isAgreeSwitch__icon }} /></>}
                />

              </div>
            </div>
          </Card>

          <Paper
            data-page-menu-key   = {'constructor'}
            data-page-menu-icon  = {<MessageOutlinedIcon />}
            data-page-menu-title = {t('titles.constructor')}
          >
            <div className={classes.constructor__content}>
              <Typography gutterBottom variant="h5" component="h2">
                {t('titles.constructor')}
              </Typography>

              <Typography variant="subtitle1" gutterBottom>
                {t('blockConstructor.selectType')}
              </Typography>

              <Typography variant="body2">
                {t('blockConstructor.description')}
              </Typography>
            </div>

            <AppBar position="static" color="default">
              <Tabs
                indicatorColor="primary"
                textColor="primary"
                onChange={this.handleChangeConstructorType}
                variant="fullWidth"
                value={this.state.constructorType}
              >
                <Tab value="fields" label={t('blockConstructor.typeFields')}/>
                <Tab value="offers" label={t('blockConstructor.typeOffers')}/>
              </Tabs>
            </AppBar>

            <div className={classes.constructor__content}>

              <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
                {t('blockConstructor.product')}
                <Tooltip title={t('blockConstructor.productHint')}>
                  <InfoOutlinedIcon color="action" classes={{ root: classes.infoIcon }} />
                </Tooltip>
              </Typography>

              <RadioGroup
                name       = "productGroup"
                value      = {selectedProductGroupId}
                onChange   = {this.handleProductGroupChange}
                className  = {classes.productGroups}
                aria-label = "productGroup"
              >
                {productGroups.filter(productGroup => productGroup.productIds.length).map(productGroup => (
                  <div key={productGroup.id} className={classes.productGroup}>
                    <FormControlLabel value={productGroup.id} control={<Radio />} label={productGroup.name} />

                    <Collapse
                      classes = {{ wrapperInner: classes.productContainer }}
                      in      = {Number(productGroup.id) === selectedProductGroupId}
                      mountOnEnter
                      unmountOnExit
                    >
                      <div className={classes.productsListContainer}>
                        <ProductSelector
                          products = {productGroup.productIds.map(productId => productsById[productId]).filter(product => product.offerIds.length)}
                          value    = {selectedProducts}
                          onChange = {this.handleProductChange}
                        />
                      </div>
                      <div className={classes.productsChipsContainer}>
                        {selectedProducts.map(id => (
                          <Chip
                            key      = {id}
                            variant  = "outlined"
                            classes  = {{root: classes.productsChip}}
                            onDelete = {this.handleProductRemove(id)}
                            label    = {productsById[id].name}
                          />
                        ))}
                      </div>
                    </Collapse>
                  </div>
                ))}
              </RadioGroup>

              {this.state.constructorType === CONSTRUCTOR_TYPE_FIELDS && (
                <Typography variant="body2" className={classes.fieldSelectionDescription}>
                  {t('blockConstructor.fieldSelectionDescription')}
                </Typography>
              )}

              {this.state.constructorType === CONSTRUCTOR_TYPE_OFFERS && (
                <Typography variant="body2" className={classes.fieldSelectionDescription}>
                  {t('blockConstructor.typeOffersDescription')}
                </Typography>
              )}
            </div>

            {this.state.constructorType === CONSTRUCTOR_TYPE_FIELDS && this.renderConstructorFields()}
            {this.state.constructorType === CONSTRUCTOR_TYPE_FIELDS && (
              <div className={classes.constructor__content}>
                <Typography gutterBottom variant="body2">
                  {t('blockConstructor.typeFieldsDescription')}
                </Typography>
              </div>
            )}
            {this.renderConstructorOffers()}
            {this.state.constructorType === CONSTRUCTOR_TYPE_OFFERS && this.renderConstructorFields()}

            {selectedOffers && selectedOffers.length ? (
              <div className={classes.constructor__content}>
                <Typography variant="subtitle1" gutterBottom classes={{root: classes.productHead}}>
                  {t('blockConstructor.geo')}
                </Typography>

                <Typography gutterBottom variant="body2">
                  {t('blockConstructor.geoDownloadDescription')}
                </Typography>

                <Button
                  component="a"
                  href={this.getGeoUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="primary"
                  variant="contained"
                >
                  {t('blockConstructor.geoDownload')}
                </Button>
              </div>
            ) : false}

          </Paper>

          <Card
            title = {t('titles.example')}
            data-page-menu-key   = {'example'}
            data-page-menu-icon  = {<CodeOutlinedIcon />}
            data-page-menu-title = {t('titles.example')}
          >
            <div className={classes.example}>
              <Typography variant="body2">
                {t('blockExample.description')}
              </Typography>

              {this.renderExampleCode()}

            </div>
          </Card>
        </PageWithMenu>
      </>
    );
  }

}

function ProductSelector({ products, value, onChange }) {
  const { t } = useTranslation(['Finance']);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState('');

  const displayedProducts = useMemo(() => {
    return filter ? products.filter(product => product?.name.toLowerCase().indexOf(filter) >= 0) : products;
  }, [products, filter]);

  const isEverythingSelected = useMemo(() => {
    return products.every(product => value.indexOf(product.id) >= 0);
  }, [products, value]);

  const open = Boolean(anchorEl);

  const handleOpen = useCallback(evt => {
    setAnchorEl(open ? null : evt.currentTarget);
  }, [open]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <>
      <Button
        size     = "small"
        variant="text"
        classes  = {{root: classes.addProductButton}}
        disabled = {isEverythingSelected}
        onClick  = {handleOpen}
        endIcon  = {<AddIcon />}
      >
        {t('blockConstructor.addProduct')}
      </Button>

      <Popover
        open         = {open}
        anchorEl     = {anchorEl}
        onClose      = {handleClose}
        anchorOrigin = {{
          vertical  : 'bottom',
          horizontal: 'center',
        }}
        transformOrigin = {{
          vertical  : 'top',
          horizontal: 'center',
        }}
      >
        <FilterCheck
          items        = {displayedProducts}
          value        = {value}
          search       = {true}
          onSearch     = {setFilter}
          onChange     = {onChange}
          isLoading    = {false}
          withHeader   = {false}
          withInfinite = {false}
        />
      </Popover>
    </>
  );
}
